import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { useCurrency } from '../../../hooks/useCurrency';
import './CurrencySelector.css';

interface Currency {
  name: string;
  code: string;
  symbol: string;
  fullName: string;
  country: string;
  countryName: string;
}

interface CurrentCurrency {
  currency: {
    isoCode: string;
    name: string;
    symbol: string;
  };
  isoCode: string;
  name: string;
}

interface CurrencySelectorProps {
  // Block settings from Shopify theme customizer
  displayStyle?: 'dropdown' | 'buttons' | 'tabs' | 'compact';
  showCurrencySymbol?: boolean;
  showCurrencyCode?: boolean;
  showCurrencyName?: boolean;
  showFlagIcons?: boolean;
  selectorLabel?: string;
  hideLabel?: boolean;
  autoRedirect?: boolean;
  rememberSelection?: boolean;
  fallbackCurrency?: string;
  
  // Animation settings
  enableAnimations?: boolean;
  animationType?: string;
  animationDuration?: number;
  animationDelay?: number;
  animationTrigger?: string;
  
  // Positioning settings
  isOverlay?: boolean;
  zIndex?: number;
  
  // Styling
  className?: string;
  
  // Block settings object (contains all the above settings)
  settings?: any;
}

export function CurrencySelector({
  displayStyle = 'dropdown',
  showCurrencySymbol = true,
  showCurrencyCode = true,
  showCurrencyName = false,
  showFlagIcons = true,
  selectorLabel = 'Currency:',
  hideLabel = false,
  autoRedirect = true,
  rememberSelection = true,
  fallbackCurrency = 'USD',
  enableAnimations = false,
  animationType = 'slide-fade-up',
  animationDuration = 800,
  animationDelay = 0,
  animationTrigger = 'onscroll',
  className = '',
  isOverlay = false,
  zIndex = 10,
  settings = {}
}: CurrencySelectorProps) {
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use currency hook for state management - this is now internal to the component
  const {
    currencies,
    currentCurrency,
    isLoading,
    error,
    setCurrency,
    formatCurrencyDisplay,
    currentCountryCode,
    currentCurrencyCode,
    currentCurrencySymbol
  } = useCurrency({
    autoInit: true,
    defaultCountry: fallbackCurrency,
    defaultCurrency: fallbackCurrency,
    defaultSymbol: '$'
  });

  // Find current currency from currencies array
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);

  useEffect(() => {
    if (currencies.length > 0 && currentCountryCode) {
      const current = currencies.find(curr => curr.country === currentCountryCode);
      setSelectedCurrency(current || null);
    }
  }, [currencies, currentCountryCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle currency selection - now internal to the component
  const handleCurrencyClick = async (currency: Currency) => {
    try {
      setIsOpen(false);
      setSelectedCurrency(currency);
      
      // Use the setCurrency from the hook
      await setCurrency(currency.country, {
        autoRedirect,
        rememberSelection,
        returnTo: window.location.pathname || '/'
      });
    } catch (err) {
      console.error('Error selecting currency:', err);
    }
  };

  // Format currency display text
  const formatCurrencyText = (currency: Currency) => {
    // Use the hook's formatCurrencyDisplay if available, otherwise fallback to manual formatting
    if (formatCurrencyDisplay) {
      return formatCurrencyDisplay(currency, {
        showSymbol: showCurrencySymbol,
        showCode: showCurrencyCode,
        showName: showCurrencyName,
        separator: ' '
      });
    }

    // Fallback manual formatting
    const parts: string[] = [];
    
    if (showCurrencySymbol && currency.symbol) {
      parts.push(currency.symbol);
    }
    
    if (showCurrencyCode && currency.code) {
      parts.push(currency.code);
    }
    
    if (showCurrencyName && currency.fullName) {
      parts.push(currency.fullName);
    }

    return parts.join(' ');
  };

  // Get flag emoji or icon
  const getFlagIcon = (countryCode: string) => {
    if (!showFlagIcons) return null;
    
    // Convert country code to flag emoji
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    
    return String.fromCodePoint(...codePoints);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className={clsx('currency-selector loading', className)}>
        {!hideLabel && <span className="currency-selector__label">{selectorLabel}</span>}
        <div className="currency-selector__loading">
          <span>Loading currencies...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={clsx('currency-selector error', className)}>
        {!hideLabel && <span className="currency-selector__label">{selectorLabel}</span>}
        <div className="currency-selector__error">
          <span>Error: {error}</span>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!currencies || currencies.length === 0) {
    return (
      <div className={clsx('currency-selector empty', className)}>
        {!hideLabel && <span className="currency-selector__label">{selectorLabel}</span>}
        <div className="currency-selector__empty">
          <span>No currencies available</span>
        </div>
      </div>
    );
  }

  // Render dropdown style
  if (displayStyle === 'dropdown') {
    return (
      <div 
        className={clsx('currency-selector dropdown', className)}
        style={isOverlay ? { zIndex } : undefined}
        ref={dropdownRef}
      >
        {!hideLabel && (
          <label className="currency-selector__label" htmlFor="currency-dropdown">
            {selectorLabel}
          </label>
        )}
        
        <div className="currency-selector__dropdown">
          <button
            id="currency-dropdown"
            className="currency-selector__trigger"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            type="button"
          >
            <div className="currency-selector__current">
              {selectedCurrency && (
                <>
                  {getFlagIcon(selectedCurrency.country) && (
                    <span className="currency-selector__flag">
                      {getFlagIcon(selectedCurrency.country)}
                    </span>
                  )}
                  <span className="currency-selector__text">
                    {formatCurrencyText(selectedCurrency)}
                  </span>
                </>
              )}
              {!selectedCurrency && (
                <span className="currency-selector__placeholder">
                  Select Currency
                </span>
              )}
            </div>
            <svg 
              className={clsx('currency-selector__arrow', { 'open': isOpen })}
              width="12" 
              height="8" 
              viewBox="0 0 12 8" 
              fill="currentColor"
            >
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {isOpen && (
            <div className="currency-selector__menu" role="listbox">
              {currencies.map((currency) => (
                <button
                  key={currency.country}
                  className={clsx('currency-selector__option', {
                    'selected': selectedCurrency?.country === currency.country
                  })}
                  onClick={() => handleCurrencyClick(currency)}
                  role="option"
                  aria-selected={selectedCurrency?.country === currency.country}
                  type="button"
                >
                  {getFlagIcon(currency.country) && (
                    <span className="currency-selector__flag">
                      {getFlagIcon(currency.country)}
                    </span>
                  )}
                  <span className="currency-selector__text">
                    {formatCurrencyText(currency)}
                  </span>
                  {selectedCurrency?.country === currency.country && (
                    <svg 
                      className="currency-selector__check"
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16" 
                      fill="currentColor"
                    >
                      <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render buttons style
  if (displayStyle === 'buttons') {
    return (
      <div className={clsx('currency-selector buttons', className)}>
        {!hideLabel && (
          <span className="currency-selector__label">{selectorLabel}</span>
        )}
        
        <div className="currency-selector__buttons">
          {currencies.map((currency) => (
            <button
              key={currency.country}
              className={clsx('currency-selector__button', {
                'selected': selectedCurrency?.country === currency.country
              })}
              onClick={() => handleCurrencyClick(currency)}
              type="button"
              aria-pressed={selectedCurrency?.country === currency.country}
            >
              {getFlagIcon(currency.country) && (
                <span className="currency-selector__flag">
                  {getFlagIcon(currency.country)}
                </span>
              )}
              <span className="currency-selector__text">
                {formatCurrencyText(currency)}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Render tabs style
  if (displayStyle === 'tabs') {
    return (
      <div className={clsx('currency-selector tabs', className)}>
        {!hideLabel && (
          <span className="currency-selector__label">{selectorLabel}</span>
        )}
        
        <div className="currency-selector__tabs" role="tablist">
          {currencies.map((currency) => (
            <button
              key={currency.country}
              className={clsx('currency-selector__tab', {
                'selected': selectedCurrency?.country === currency.country
              })}
              onClick={() => handleCurrencyClick(currency)}
              role="tab"
              aria-selected={selectedCurrency?.country === currency.country}
              type="button"
            >
              {getFlagIcon(currency.country) && (
                <span className="currency-selector__flag">
                  {getFlagIcon(currency.country)}
                </span>
              )}
              <span className="currency-selector__text">
                {formatCurrencyText(currency)}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Render compact style
  if (displayStyle === 'compact') {
    return (
      <div className={clsx('currency-selector compact', className)}>
        {!hideLabel && (
          <span className="currency-selector__label">{selectorLabel}</span>
        )}
        
        <div className="currency-selector__compact">
          {currencies.map((currency, index) => (
            <React.Fragment key={currency.country}>
              {index > 0 && <span className="currency-selector__separator">|</span>}
              <button
                className={clsx('currency-selector__compact-item', {
                  'selected': selectedCurrency?.country === currency.country
                })}
                onClick={() => handleCurrencyClick(currency)}
                type="button"
              >
                {getFlagIcon(currency.country) && (
                  <span className="currency-selector__flag">
                    {getFlagIcon(currency.country)}
                  </span>
                )}
                <span className="currency-selector__text">
                  {formatCurrencyText(currency)}
                </span>
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  // Fallback to dropdown if displayStyle is unknown
  return (
    <div className={clsx('currency-selector', className)}>
      <span>Unsupported display style: {displayStyle}</span>
    </div>
  );
}

export default CurrencySelector;
